import { ChevronRight } from 'lucide-react'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@klear/ui/components/collapsible'
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem
} from '@klear/ui/components/sidebar'
import { Link, useLocation, useParams } from 'react-router'
import { pathMatchesItems } from '@/lib/nav'
import type { NavItem } from '@/lib/nav'
import { useAuth } from '@klear/ui/contexts/auth-context'
import { PERMISSION_ACTION_VIEW, buildNavPathTrader } from '@klear/constants'

export function NavMain({ items, title }: { items: NavItem[]; title?: string }) {
  const { user } = useAuth()
  const { traderId } = useParams()
  const { pathname } = useLocation()

  return (
    <SidebarGroup>
      {title && <SidebarGroupLabel>{title}</SidebarGroupLabel>}
      <SidebarMenu>
        {items
          .filter((item) => (item.permission ? user?.hasPermission(item.permission, PERMISSION_ACTION_VIEW) : true))
          .map((item) => (
            <Collapsible
              key={item.title}
              asChild
              defaultOpen={pathMatchesItems(pathname, traderId!, item.url)}
              className="group/collapsible"
            >
              <SidebarMenuItem>
                {item.url && !item.items?.length ? (
                  <Link to={item.url.startsWith('http') ? item.url : buildNavPathTrader(traderId!, item.url)}>
                    <SidebarMenuButton tooltip={item.title} isActive={pathMatchesItems(pathname, traderId!, item.url)}>
                      {item.icon && <item.icon />}
                      <span>{item.title}</span>
                    </SidebarMenuButton>
                  </Link>
                ) : (
                  <CollapsibleTrigger asChild>
                    <SidebarMenuButton tooltip={item.title}>
                      {item.icon && <item.icon />}
                      <span>{item.title}</span>
                      {item.items && item.items.length > 0 && (
                        <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                      )}
                    </SidebarMenuButton>
                  </CollapsibleTrigger>
                )}
                {item.badge != null && item.badge > 0 && (
                  <SidebarMenuBadge className="top-1.5 rounded-full bg-primary text-primary-foreground peer-hover/menu-button:text-primary-foreground peer-data-[active=true]/menu-button:text-primary-foreground">
                    <span aria-hidden>{item.badge}</span>
                    <span className="sr-only">{`${item.badge} to action`}</span>
                  </SidebarMenuBadge>
                )}
                {item.items && item.items.length > 0 && (
                  <CollapsibleContent>
                    <SidebarMenuSub>
                      {item.items
                        ?.filter((subItem) =>
                          subItem.permission ? user?.hasPermission(subItem.permission, PERMISSION_ACTION_VIEW) : true
                        )
                        ?.map((subItem) => (
                          <SidebarMenuSubItem key={subItem.title}>
                            <SidebarMenuSubButton
                              asChild
                              isActive={pathMatchesItems(pathname, traderId!, item.url, subItem.url)}
                            >
                              <Link to={buildNavPathTrader(traderId!, item.url, subItem.url)}>
                                <span>{subItem.title}</span>
                              </Link>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                        ))}
                    </SidebarMenuSub>
                  </CollapsibleContent>
                )}
              </SidebarMenuItem>
            </Collapsible>
          ))}
      </SidebarMenu>
    </SidebarGroup>
  )
}
